import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'
import ReactMarkdown from 'react-markdown'
import ProgressBar from './ProgressBar'
import InputComponent from '../InputComponent'
import { File, Play } from 'lucide-react'
import { downloadHsCompassResult } from '../shared/pdf/downloadHsCompassResult'
import {
    hydrateHsCompassChat,
    sendHsCompassMessage,
    startHsCompassConversation,
    subscribeHsCompassChat,
    updateHsCompassDraft,
    uploadHsCompassInvoice,
} from './hsCompassRuntime'

const WAITING_MESSAGES = [
    "Analyzing product classification details…",
    "Reviewing tariff structure for the selected country…",
    "Cross-checking international HS nomenclature…",
    "Looking for the closest matching product category…",
    "Validating import/export compliance rules…",
    "Filtering potential HS codes based on your product description…",
    "Matching product attributes with official tariff guidelines…",
    "Consulting the harmonized system reference tables…",
    "Refining classification by industry-specific usage…",
    "Performing additional mapping to confirm accuracy…",
    "Ensuring consistency with trade regulations for your region…",
    "Verifying duty implications for the shortlisted codes…",
    "Confirming final recommendation with reference datasets…",
    "Classifying product…",
    "Checking tariff tables…",
    "Reviewing HS categories…",
    "Mapping product details…",
    "Verifying country rules…",
    "Confirming classification…",
    "Searching closest match…",
    "Evaluating trade codes…",
    "Cross-referencing data…",
    "Preparing recommendation…",
    "Gathering reference data for this product category…",
    "Reviewing customs descriptions for close matches…",
    "Assessing product composition and intended use…",
    "Comparing classification rules across trade zones…",
    "Evaluating potential duty and compliance implications…",
    "Mapping your description to standardized HS terminology…",
    "Confirming whether additional subheadings apply…",
    "Checking regional exceptions and special provisions…",
    "Refining the shortlist of eligible HS codes…",
    "Ensuring the suggested code aligns with current guidelines…",
    "Finalizing classification — this may take a moment…",
    "Preparing the most suitable HS code recommendation…",
    "Processing request…",
    "Reviewing product specs…",
    "Scanning tariff index…",
    "Checking classification rules…",
    "Comparing code variations…",
    "Refining match results…",
    "Evaluating alternatives…",
    "Confirming eligibility…",
    "Consolidating findings…",
    "Almost ready…",
    "Identifying the correct HS heading for your product…",
    "Reviewing explanatory notes for deeper classification guidance…",
    "Comparing similar classifications to avoid miscategorization…",
    "Checking whether your product falls under a special tariff chapter…",
    "Analyzing material type, function, and trade intent…",
    "Confirming whether any exclusions apply to this product…",
    "Retrieving updated classification references from trade databases…",
    "Ensuring your product matches the legal text interpretation…",
    "Validating classification approach with official chapter notes…",
    "Resolving borderline matches between related tariff categories…",
    "Running a final compliance cross-check before displaying results…"
]

// Fetching configuration from environment variables
// Note: It's common practice to use REACT_APP_ prefix for create-react-app style environments.
const AI_HS_BASE_URL = process.env.REACT_APP_HSCOMPASS_API_BASE_URL
const LOGIN_USERNAME = process.env.REACT_APP_HSCODE_API_LOGIN_USERNAME
const LOGIN_PASSWORD = process.env.REACT_APP_HSCODE_API_LOGIN_PASSWORD
const MAX_UPLOAD_MB = 10
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024

const AiHsCalculatorChat = ({ activeChat, onPersistChat, onCreateNewChat, setResultData }) => {
    const [messages, setMessages] = useState([])
    const [finalRes, setFinalRes] = useState(null)
    const [input, setInput] = useState("")
    const [sessionId, setSessionId] = useState(null)
    const [options, setOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [progress, setProgress] = useState(0)
    const [showProgress, setShowProgress] = useState(false)
    const [waiting, setWaiting] = useState("")
    const [authToken, setAuthToken] = useState(null) // State to store JWT token
    const [itemName, setItemName] = useState("")
    const [targetCountry, setTargetCountry] = useState("")
    const [conversationStarted, setConversationStarted] = useState(false)
    const [selectedFile, setSelectedFile] = useState(null)
    const [itemError, setItemError] = useState("")
    const [countryError, setCountryError] = useState("")
    const [autoNoSent, setAutoNoSent] = useState(false)
    const scrollRef = useRef(null)
    const inputRef = useRef(null)
    const uploadSelectedFileRef = useRef(null)
    const handleSendMessageRef = useRef(null)

    const progressInterval = useRef(null)
    const waitingInterval = useRef(null)
    const preFileInputRef = useRef(null)

    useEffect(() => {
        if (!activeChat?._id) return

        const applySnapshot = (snapshot) => {
            setMessages(Array.isArray(snapshot?.messages) ? snapshot.messages : [])
            setFinalRes(snapshot?.finalRes || null)
            setInput("")
            setSessionId(snapshot?.sessionId || null)
            setOptions(Array.isArray(snapshot?.options) ? snapshot.options : [])
            setLoading(Boolean(snapshot?.loading))
            setProgress(Number(snapshot?.progress || 0))
            setShowProgress(Boolean(snapshot?.showProgress))
            setWaiting(snapshot?.waiting || "")
            setItemName(snapshot?.itemName || "")
            setTargetCountry(snapshot?.targetCountry || "")
            setConversationStarted(Boolean(snapshot?.conversationStarted))
            setSelectedFile(snapshot?.selectedFile || null)
            setItemError("")
            setCountryError("")
            setAutoNoSent(Boolean(snapshot?.autoNoSent))
        }

        applySnapshot(hydrateHsCompassChat(activeChat))
        return subscribeHsCompassChat(activeChat._id, applySnapshot)
    }, [activeChat])

    useEffect(() => {
        setResultData(finalRes || null)
    }, [finalRes, setResultData])

    useEffect(() => {
        if (!activeChat?._id) return

        onPersistChat?.({
            ...activeChat,
            messages,
            finalRes,
            sessionId,
            options,
            itemName,
            targetCountry,
            conversationStarted,
            autoNoSent,
        })
    }, [
        activeChat,
        messages,
        finalRes,
        sessionId,
        options,
        itemName,
        targetCountry,
        conversationStarted,
        autoNoSent,
        onPersistChat,
    ])

    // --- Login Function ---
    // Made login standalone so it can be called cleanly
    const login = useCallback(async () => {
        try {
            const res = await fetch(`${AI_HS_BASE_URL}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: LOGIN_USERNAME,
                    password: LOGIN_PASSWORD
                }),
            })

            const data = await res.json()
            if (res.ok && data.token) { // Changed data.success && data.token to data.token for robustness based on the successful response structure
                setAuthToken(data.token)
                try { localStorage.setItem('hsCompassAuthToken', data.token) } catch (_) { /* ignore */ }
                // console.log('Login successful, token retrieved.')
                return data.token
            } else {
                // Throw error for status code > 200 or missing token
                throw new Error(data.message || 'Login failed: Token not received.')
            }
        } catch (error) {
            console.error('Error during login:', error)
            toast.error('Authentication failed. Cannot start chat.')
            return null
        }
    }, [])

    // --- Progress Bar Control (optimized) ---
    const startProgress = useCallback(() => {
        if (progressInterval.current) {
            clearInterval(progressInterval.current)
        }
        setProgress(0)
        setShowProgress(true)
        progressInterval.current = setInterval(() => {
            setProgress(prev => prev < 85 ? prev + Math.random() : prev)
        }, 400)
    }, [])

    const finishProgress = useCallback((callback) => {
        if (progressInterval.current) {
            clearInterval(progressInterval.current)
            progressInterval.current = null
        }
        setProgress(100)
        // inputRef.current.focus()

        setTimeout(() => {
            setShowProgress(false)
            setProgress(0)
            callback?.()
        }, 800)
    }, [])

    // --- Response Handler (optimized with batched updates) ---
    const handleResponses = useCallback((data) => {
        if (!data) {
            finishProgress(() => setLoading(false))
            return
        }

        if (data.sessionId && data.sessionId !== sessionId) {
            setSessionId(data.sessionId)
        }

        const newReply = {
            sender: 'bot',
            message: data
        }

        const { conversationState } = data

        switch (conversationState) {
            case 'product_confirmation':
                if (selectedFile && !autoNoSent) {
                    if (!showProgress) startProgress()
                    setOptions([])
                    setAutoNoSent(true)
                    setTimeout(() => handleSendMessageRef.current?.("No, this is not correct", true), 0)
                } else {
                    if (!showProgress) startProgress()
                    finishProgress(() => {
                        setMessages(prev => [...prev, newReply])
                        setOptions(data.options || [])
                        setLoading(false)
                    })
                }
                break

            case 'awaiting_country':
            case 'initial':
                if (!showProgress) startProgress()
                finishProgress(() => {
                    setMessages(prev => [...prev, newReply])
                    setOptions(data.options || [])
                    setLoading(false)
                })
                break

            case 'product_selection':
                if (!showProgress) startProgress()
                finishProgress(() => {
                    setMessages(prev => [...prev, newReply])
                    setLoading(false)
                })
                break

            case 'datasheet_hs4_processing':
            case 'hs4_processing':
                if (!showProgress) startProgress()
                finishProgress(() => {
                    setMessages(prev => [...prev, newReply])
                    setOptions(data.options || [])
                    setLoading(false)
                })
                break

            case 'awaiting_file_upload':
                if (selectedFile) {
                    if (!showProgress) startProgress()
                    setOptions([])
                    setTimeout(() => { uploadSelectedFileRef.current?.() }, 0)
                } else {
                    if (!showProgress) startProgress()
                    finishProgress(() => {
                        setMessages(prev => [...prev, newReply])
                        setOptions(["Upload Invoice"])
                        setLoading(false)
                    })
                }
                break

            // case 'hs4_completed':
            // case 'datasheet_hs4_completed':
            //     finishProgress(() => {
            //         setMessages(prev => [...prev, newReply])
            //         setOptions(data.options || [])
            //         setLoading(false)
            //     })
            //     break

            case 'hs4_completed':
            case 'datasheet_hs4_completed':
                // Skip showing HS4 result and directly trigger final classification
                handleSendMessageRef.current?.("Yes", true)
                break

            case 'wco_analysis_completed':
                setProgress(65)
                handleSendMessageRef.current?.("Yes", true)
                break

            case 'classification_completed':
                finishProgress(() => {
                    setMessages(prev => [...prev, newReply])
                    setOptions(data.options || [])
                    setFinalRes(data?.structuredData)
                    setResultData?.(data.structuredData)
                    setLoading(false)
                })
                break

            default:
                finishProgress(() => setLoading(false))
                break
        }
    }, [showProgress, startProgress, finishProgress, setResultData, sessionId, selectedFile, autoNoSent])

    // Ensure we have a valid server session before actions that require it
    const ensureSession = useCallback(async () => {
        if (sessionId) return sessionId
        if (!authToken) return null

        // Generate a client session id and register it with the server
        const genId = () => {
            try {
                if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
                    return window.crypto.randomUUID()
                }
            } catch (e) { /* ignore */ }
            return `${Date.now()}-${Math.floor(Math.random() * 1e6)}`
        }
        const generated = `session-${genId()}`
        setSessionId(generated)
        try {
            // Fire-and-forget: don't block UX on this registration request
            fetch(`${AI_HS_BASE_URL}/api/chat/message`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({ message: 'start', sessionId: generated })
            }).catch((e) => console.error('Failed to register session (will retry on next action):', e))
        } catch (e) {
            console.error('Failed to register session (will retry on next action):', e)
        }
        return generated
    }, [authToken, sessionId])

    // --- Send Message Handler (optimized) ---
    const handleSendMessage = useCallback(async (inputValue, isAuto = false, providedSid = null) => {
        if (!inputValue?.trim()) return
        if (!activeChat?._id) return
        if (!authToken) {
            toast.error('Authentication token is missing. Please log in again.')
            return
        }

        if (!isAuto) setInput("")
        await sendHsCompassMessage(activeChat._id, inputValue, authToken, { isAuto, providedSid })
    }, [activeChat?._id, authToken]) // Include authToken in dependency array

    // Keep a stable reference for use in earlier callbacks
    useEffect(() => {
        handleSendMessageRef.current = handleSendMessage
    }, [handleSendMessage])

    // --- Input Handlers (optimized) ---
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter" && input.trim() !== "") {
            handleSendMessage(input)
        }
    }, [input, handleSendMessage])

    const handleInvoiceUpload = useCallback(async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        if (!authToken) {
            toast.error('Authentication token is missing. Please log in again.')
            return
        }

        // Require item name and target country before any upload
        if (!itemName.trim() || !targetCountry.trim()) {
            toast.error('Please enter Item Name and Target Country before uploading.')
            if (!itemName.trim()) setItemError('Please enter item name')
            if (!targetCountry.trim()) setCountryError('Please enter target country')
            e.target.value = ''
            return
        }

        const allowedExt = ['pdf']
        const ext = file.name.split('.').pop()?.toLowerCase()
        if (!ext || !allowedExt.includes(ext)) {
            toast.error('Unsupported file type. Only PDF is allowed.')
            e.target.value = ''
            return
        }

        if (file.size > MAX_UPLOAD_BYTES) {
            toast.error(`File too large. Max size is ${MAX_UPLOAD_MB} MB.`)
            e.target.value = ''
            return
        }

        await uploadHsCompassInvoice(activeChat?._id, file, authToken)
        e.target.value = ''
        return
    }, [activeChat?._id, authToken, itemName, targetCountry]) // Include dependencies

    // Upload the pre-selected file (if any) after Analyze & Classify
    const uploadSelectedFile = useCallback(async () => {
        if (!selectedFile) return
        if (!authToken) return

        const body = new FormData()
        body.append('file', selectedFile)
        body.append('itemName', itemName.trim())
        body.append('targetCountry', targetCountry.trim())

        setLoading(true)
        if (!showProgress) startProgress()
        setMessages(prev => [...prev, {
            sender: 'user',
            message: { message: `Uploading Invoice ${selectedFile.name}` }
        }])

        try {
            let sid = sessionId
            if (!sid) sid = await ensureSession()
            if (!sid && !sessionId) throw new Error('No active chat session. Please start analysis first.')

            body.append('sessionId', sid || sessionId)
            const res = await fetch(`${AI_HS_BASE_URL}/api/chat/upload-datasheet`, {
                method: 'POST',
                body,
                headers: { 'Authorization': `Bearer ${authToken}` }
            })

            if (!res.ok) {
                let detail = 'Upload failed'
                try { const errJson = await res.clone().json(); detail = errJson?.message || errJson?.error || JSON.stringify(errJson) } catch (_) { try { detail = await res.text() } catch (_) { } }
                throw new Error(detail)
            }

            const data = await res.json()
            if (data?.data?.sessionId) setSessionId(data.data.sessionId)
            handleResponses(data.data)
        } catch (error) {
            console.error('Error uploading invoice:', error)
            toast.error(`Failed to upload invoice: ${error?.message || 'Unknown error'}`)
            finishProgress(() => setLoading(false))
        } finally {
            setSelectedFile(null)
        }
    }, [selectedFile, authToken, itemName, targetCountry, sessionId, ensureSession, startProgress, finishProgress, handleResponses, showProgress])

    // Keep a stable reference for use in earlier callbacks
    useEffect(() => {
        uploadSelectedFileRef.current = uploadSelectedFile
    }, [uploadSelectedFile])

    const handleSelectOption = useCallback((option) => {
        handleSendMessage(option)
    }, [handleSendMessage])

    // --- Start Conversation Handler ---
    const handleStartConversation = useCallback(async () => {
        if (!itemName.trim() || !targetCountry.trim()) {
            toast.error('Please enter both item name and target country')
            return
        }

        await startHsCompassConversation(activeChat?._id, authToken)
    }, [activeChat?._id, authToken, itemName, targetCountry])

    const handleEndConversation = useCallback(async (option) => {
        if (option === 'Export classification results') {
            const blob = await downloadHsCompassResult(finalRes);
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `HS_Compass_Results_${new Date().toLocaleDateString('en-GB')}.pdf`;
            link.click();
        } else if (option === 'Classify another product') {
            await onCreateNewChat?.()
        }
    }, [finalRes, onCreateNewChat])



    // --- Render Options (optimized with early return) ---
    const lastMessage = messages[messages.length - 1]
    const conversationState = lastMessage?.message?.conversationState
    const currentChatTitle = itemName.trim() && targetCountry.trim()
        ? `${itemName.trim()} - ${targetCountry.trim()}`
        : activeChat?.title || 'New chat'
    const shouldCompactChatBody = messages.length === 0 && !loading && options.length === 0 && !showProgress
    const chatBodyClassName =
  'flex-1 relative w-full flex flex-col gap-4 mt-2 pr-1 overflow-y-auto'

    const renderOptions = useMemo(() => {
        if (options.length === 0) return null

        switch (conversationState) {
            case "awaiting_file_upload":
                return (
                    <label htmlFor="invoice" onMouseDown={(e) => {
                        if (!itemName.trim() || !targetCountry.trim()) {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!itemName.trim()) setItemError('Please enter item name')
                            if (!targetCountry.trim()) setCountryError('Please enter target country')
                            toast.warn('Please fill Item Name and Target Country before uploading.');
                        }
                    }} className={`bg-[#03336e] text-white px-4 py-2 rounded-md cursor-pointer hover:bg-[#024a9e] transition-colors ${!authToken ? 'opacity-50 pointer-events-none' : ''}`}>
                        <span>Upload Datasheet (PDF, up to {MAX_UPLOAD_MB} MB)</span>
                        <input
                            onChange={handleInvoiceUpload}
                            type="file"
                            name="invoice"
                            id="invoice"
                            className='hidden'
                            accept=".pdf"
                            disabled={!authToken}
                        />
                    </label>
                )
            // case 'product_selection':
            //     return options.map((item, idx) => (
            //         <div key={idx} className="flex flex-col items-start justify-center gap-2 p-4 border rounded-lg">
            //             <p className='font-semibold text-xl'>{item.product_name}</p>
            //             <p>
            //                 <span className='font-semibold'>Specifications: </span>
            //                 <span>{item.specifications}</span>
            //             </p>
            //             {item.keyFeatures?.length > 0 && (
            //                 <div className="flex items-center gap-2 flex-wrap">
            //                     {item.keyFeatures.map((feature, idx) => (
            //                         <span key={idx} className="bg-gray-100 px-2 py-1 rounded text-sm">
            //                             {feature}
            //                         </span>
            //                     ))}
            //                 </div>
            //             )}
            //             <button
            //                 type="button"
            //                 onClick={() => handleSelectOption(item.product_name)}
            //                 className='w-full bg-[#03336e] text-white rounded py-2 hover:bg-[#024a9e] transition-colors'
            //             >
            //                 Select Product
            //             </button>
            //         </div>
            //     ))

            case 'classification_completed':
                return (
                    <div className="flex items-center gap-2  flex-wrap ">
                        {options.map((option, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleEndConversation(option)}
                                className='w-fit whitespace-nowrap text-white border bg-[#03336e] hover:bg-[#024a9e] transition-colors flex items-center justify-center gap-2 shadow-lg rounded-full p-1 font-medium px-4 py-1'
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                )
            default:
                return options.map((option, idx) => (
                    <button
                        key={idx}
                        onClick={() => handleSelectOption(option)}
                        className='w-fit whitespace-nowrap text-white border bg-[#03336e] hover:bg-[#024a9e] transition-colors flex items-center justify-center gap-2 shadow-lg rounded-full p-1 font-medium px-4 py-1'
                    >
                        {option}
                    </button>
                ))
        }
    }, [options, conversationState, handleInvoiceUpload, handleSelectOption, authToken, handleEndConversation, itemName, targetCountry])


    // Initialize auth (session will be opened lazily on first action)
    // useEffect(() => {
    //     const initializeChat = async () => {
    //         // Restore token from storage if available; else login
    //         let restored = null
    //         try { restored = localStorage.getItem('hsCompassAuthToken') } catch (_) { /* ignore */ }
    //         if (restored) {
    //             setAuthToken(restored)
    //         } else {
    //             await login()
    //         }
    //     }
    //     initializeChat()
    // }, [login])

    useEffect(() => {
        const initializeChat = async () => {
            let restored = null
            try {
                restored = localStorage.getItem('hsCompassAuthToken')
            } catch (_) { }

            if (restored) {
                setAuthToken(restored)

                // 🔥 Validate token with lightweight call
                const isValid = await validateToken(restored)

                if (!isValid) {
                    const newToken = await login()
                    setAuthToken(newToken)
                }
            } else {
                const newToken = await login()
                setAuthToken(newToken)
            }
        }

        initializeChat()
    }, [login])

    const validateToken = async (token) => {
        try {
            const res = await fetch(`${AI_HS_BASE_URL}/api/user`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })

            return res.ok
        } catch {
            return false
        }
    }

    useEffect(() => {
        if (!loading) return

        waitingInterval.current = setInterval(() => {
            const randomIndex = Math.floor(Math.random() * WAITING_MESSAGES.length)
            setWaiting(WAITING_MESSAGES[randomIndex])
        }, 4000)

        return () => {
            if (waitingInterval.current) {
                clearInterval(waitingInterval.current)
            }
        }
    }, [loading])
    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages])

    // --- Cleanup on unmount ---
    useEffect(() => {
        return () => {
            if (progressInterval.current) clearInterval(progressInterval.current)
            if (waitingInterval.current) clearInterval(waitingInterval.current)
        }
    }, [])

    // --- UI ---
    return (
          <div className="w-full h-full flex flex-col gap-2 px-4 py-2">
            <div className="w-full flex flex-col items-center justify-center gap-2">
                <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between border-b py-2">
                    <div>
                        <h3 className='font-bold'>Talk with Union CodeSense™</h3>
                        <p className="text-xs text-slate-500 mt-1">{currentChatTitle}</p>
                    </div>
                </div>
            </div>

            <div className="w-full flex flex-col md:flex-row items-center justify-center gap-2">
                <InputComponent
                    label={"Item Name"}
                    type="text"
                    value={itemName}
                    onChange={(e) => {
                        const value = e.target.value
                        setItemName(value)
                        updateHsCompassDraft(activeChat?._id, { itemName: value })
                        if (value?.trim()) setItemError("")
                    }}
                    disabled={conversationStarted}
                    className={"!py-5 rounded-xl"}
                    placeholder={"Industrial SSD 2TB NVMe"}
                    error={itemError}
                />
                <InputComponent
                    label={"Target Country"}
                    type="text"
                    value={targetCountry}
                    onChange={(e) => {
                        const value = e.target.value
                        setTargetCountry(value)
                        updateHsCompassDraft(activeChat?._id, { targetCountry: value })
                        if (value?.trim()) setCountryError("")
                    }}
                    disabled={conversationStarted}
                    className={"!py-5 rounded-xl"}
                    placeholder={"India, China, EU, USA, etc"}
                    error={countryError}
                />
            </div>
            {!conversationStarted && (
                <div className="w-full flex flex-col md:flex-row items-center justify-start gap-3 cursor-pointer">
                    <label
                        htmlFor="file"
                        onMouseDown={(e) => {
                            // Prevent opening picker if fields are empty
                            if (!itemName.trim() || !targetCountry.trim()) {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!itemName.trim()) setItemError('Please enter item name')
                                if (!targetCountry.trim()) setCountryError('Please enter target country')
                                toast.warn('Please fill Item Name and Target Country before uploading.');
                                return false
                            }
                            return true
                        }}
                        className={`w-full md:w-fit whitespace-nowrap text-black border flex items-center justify-center gap-1 rounded-xl  font-medium px-4 py-2 cursor-pointer shadow hover:shadow-xl  duration-300 transition-all ${!authToken ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                        <input
                            type="file"
                            name="file"
                            id="file"
                            className="hidden"
                            onChange={handleInvoiceUpload}
                            accept=".pdf"
                            ref={preFileInputRef}
                            disabled={!authToken}
                        />
                        <File className='fill-current' size={20} />
                        {selectedFile ? `Selected: ${selectedFile.name}` : `Upload Datasheet — OUS Parse™ (PDF, up to ${MAX_UPLOAD_MB} MB)`}
                    </label>
                    <button
                        onClick={handleStartConversation}
                        type="button"
                        disabled={!itemName.trim() || !targetCountry.trim() || !authToken}
                        className='w-full md:w-fit whitespace-nowrap shadow hover:shadow-xl  duration-300 transition-all btnBg flex items-center justify-center gap-2 rounded-xl font-medium px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        <Play size={18} strokeWidth={1.5} className='fill-current' />
                        Analyze & Classify
                    </button>
                </div>
            )}

            <div className={chatBodyClassName}>
                <p className='w-fit md:max-w-[70%] max-w-[85%] self-start text-black bg-gray-100 shadow-lg ml-2 flex items-center justify-center gap-2 rounded-xl p-1 font-medium px-4 py-1'>
                    Hello 👋 I'm Union CodeSense™. Upload a datasheet or invoice, or type the item name and target country for a local HS code.
                </p>

                {messages.map((msg, idx) => (
                    <div
                        key={idx}
                        className={`w-fit md:max-w-[70%] max-w-[85%] flex flex-col shadow-lg mr-2 items-start justify-center gap-2 rounded-xl p-1 font-medium px-4 py-1 ${msg.sender === "user"
                            ? "self-end bg-[#03336e] text-white"
                            : "self-start text-black bg-gray-100"
                            } `}
                    >
                        <ReactMarkdown>{msg.message.message}</ReactMarkdown>
                    </div>
                ))}
                <div className="" ref={scrollRef} />


                {renderOptions && (
                    <div className="sticky flex items-center gap-2 flex-wrap bottom-0 p-4 w-full bg-white">
                        {renderOptions}
                    </div>
                )}
            </div>

            {loading && (
                <p className='animate-pulse text-gray-600 ml-2 self-start'>
                    {waiting}
                </p>
            )}
            {showProgress && <ProgressBar value={progress} />}

            <div className="w-full flex items-center gap-2 border p-2 rounded-xl">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    type="text"
                    autoFocus
                    ref={inputRef}
                    className='w-full h-8 p-2 text-sm rounded-md outline-none active:border-cus-blue focus:border-cus-blue'
                    placeholder='Type your message'
                    disabled={loading || !authToken}
                />
                <button
                    onClick={() => handleSendMessage(input)}
                    type="button"
                    disabled={loading || !input.trim() || !authToken}
                    className='w-fit md:w-fit whitespace-nowrap shadow hover:shadow-xl duration-300 transition-all btnBg flex items-center justify-center gap-2 rounded-xl font-medium px-4 py-1 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                    Send
                </button>
            </div>
        </div>
    )
}

export default AiHsCalculatorChat
